import React from 'react';
import './PrivicyPolicy.css';

const policySections = [
  {
    id: 'scope',
    title: '1. Scope and who we are',
    paragraphs: [
      'This Privacy Policy explains how epicfinal ("epicfinal", "we", "us" or the "Company") collects, uses, stores, shares and protects personal information when you visit epicfinal.com, contact us, open an account, purchase or use our hosting, cloud, managed services, software development, GenAI or related services.',
      'For personal information collected through our website, sales process, customer relationship and business operations, epicfinal is generally the database controller. This Policy forms part of our Terms and Conditions and should be read together with any order form, service agreement or product-specific notice that applies to you.'
    ]
  },
  {
    id: 'information-you-provide',
    title: '2. Information you provide to us',
    paragraphs: [
      'You may provide personal information when you request a quote, schedule a meeting, submit a form, register for an account, purchase a service, contact support, subscribe to updates or otherwise communicate with us.'
    ],
    bullets: [
      'Identity and contact details, such as name, business name, role, email address, telephone number and address.',
      'Account and authentication details, such as username, account identifiers and security credentials.',
      'Commercial and transaction details, such as selected services, order information, invoices, payment status and billing correspondence.',
      'Support and communications content, including requests, messages, call or meeting details, technical information and feedback.',
      'Project and service information, including configurations, domains, infrastructure details, code, files, prompts, application content and other material you choose to provide.'
    ]
  },
  {
    id: 'collection-notice',
    title: '3. Important notice when providing personal information',
    paragraphs: [
      'You are generally not legally required to provide personal information to epicfinal, and providing it is based on your choice and consent, except where information is required by law or under a contractual obligation. However, if you do not provide information marked as required, we may be unable to respond to your request, create or administer your account, process an order, provide support or deliver the requested service.',
      'The purposes for which we request information, the categories of recipients that may receive it, and your rights regarding the information are described in this Policy. Please provide accurate, complete and up-to-date information. If you provide information about another person, you must have lawful authority to do so and provide any required notice to that person.'
    ],
    callout: true
  },
  {
    id: 'automatic-information',
    title: '4. Information collected automatically',
    paragraphs: [
      'When you use our website or services, we and service providers acting on our behalf may automatically collect technical and usage information.'
    ],
    bullets: [
      'IP address, approximate location, browser type, operating system, device type and language settings.',
      'Pages viewed, referral source, interactions, session dates and times, and performance or diagnostic data.',
      'Cookie identifiers, advertising identifiers and similar online technologies.',
      'Service logs, security events, access records, API activity, resource usage and information required to operate, monitor and secure our systems.'
    ]
  },
  {
    id: 'customer-data',
    title: '5. Customer Data and our role as a service provider',
    paragraphs: [
      'Our customers may upload, host, process or otherwise make available personal information relating to their own users, employees, customers or other individuals ("Customer Data"). Where epicfinal processes Customer Data solely to provide services on a customer\'s behalf, the customer is generally responsible for determining the purposes and means of processing and for providing required notices, obtaining required permissions and ensuring that its use of the services complies with applicable law.',
      'In those circumstances, epicfinal processes Customer Data under the customer\'s documented instructions, the applicable agreement and our legal obligations. Individuals seeking to exercise rights regarding Customer Data should normally contact the relevant customer first.'
    ]
  },
  {
    id: 'uses',
    title: '6. How we use personal information',
    bullets: [
      'To provide, activate, administer, maintain, support and improve our website and services.',
      'To identify and authenticate users, manage accounts, orders, subscriptions, billing and customer relationships.',
      'To respond to inquiries, provide technical support and communicate service, security and operational notices.',
      'To personalize content, measure performance, understand usage and develop new or improved services.',
      'To protect systems, users and customers; detect fraud, abuse, vulnerabilities and security incidents; and enforce our agreements.',
      'To send marketing communications where permitted, subject to your choices and applicable law.',
      'To comply with legal obligations, lawful requests, court orders and regulatory requirements, and to establish or defend legal claims.'
    ]
  },
  {
    id: 'sharing',
    title: '7. When we share information',
    paragraphs: [
      'We do not sell personal information. We may disclose information only where reasonably necessary for the purposes described in this Policy, including to:'
    ],
    bullets: [
      'Infrastructure, hosting, cloud, cybersecurity, monitoring, backup, communications and support providers.',
      'Business tools and integrations used to provide our services, including scheduling, customer relationship management, analytics, advertising, embedded media and payment providers. Depending on the feature used, these may include Google, HubSpot, YouTube and PayPal.',
      'Professional advisers such as accountants, auditors, insurers and legal advisers who are subject to appropriate confidentiality duties.',
      'Competent authorities, courts or other parties where disclosure is required by law, needed to protect rights or safety, or necessary to investigate misuse or security incidents.',
      'A purchaser, successor or relevant counterparty in connection with a merger, acquisition, financing, reorganization or transfer of all or part of our business, subject to appropriate safeguards.',
      'Other parties when you direct us to share information or provide your consent.'
    ]
  },
  {
    id: 'international-transfers',
    title: '8. Storage and transfers outside Israel',
    paragraphs: [
      'Our systems and service providers may store or process information in Israel and in other countries where they or their subcontractors operate. Privacy and data protection laws in those countries may differ from Israeli law.',
      'Where personal information is transferred outside Israel, we take reasonable steps intended to ensure that the transfer is made in accordance with the Protection of Privacy Regulations (Transfer of Data to Databases Abroad), 2001 and other applicable requirements, including through contractual commitments or another recognized transfer mechanism where required.'
    ]
  },
  {
    id: 'cookies',
    title: '9. Cookies and similar technologies',
    paragraphs: [
      'We use cookies and similar technologies to operate the website, remember preferences, protect accounts, understand website performance, measure campaigns and enable third-party features. These technologies may include:'
    ],
    bullets: [
      'Strictly necessary cookies used for security, account access and essential website functions.',
      'Preference and functionality cookies that remember choices and support embedded features.',
      'Analytics and advertising technologies, including Google services, used to understand traffic and measure campaign effectiveness.',
      'Third-party cookies or local storage used when you interact with services such as HubSpot, YouTube or PayPal.'
    ],
    closing: 'You can control or delete cookies through your browser settings. Blocking certain cookies may prevent parts of the website or services from working correctly. Third-party technologies are also governed by the privacy policies of their respective providers.'
  },
  {
    id: 'retention',
    title: '10. How long we retain information',
    paragraphs: [
      'We retain personal information only for as long as reasonably necessary for the purposes for which it was collected, including to provide services, maintain business and security records, comply with tax, accounting and legal obligations, resolve disputes and enforce agreements. Retention periods vary according to the type of information, the service involved, legal requirements and operational or security needs.',
      'When information is no longer required, we may delete, anonymize or securely archive it, subject to applicable law and technical limitations. Backup copies may remain for a limited period until they are overwritten through normal backup cycles.'
    ]
  },
  {
    id: 'security',
    title: '11. Information security',
    paragraphs: [
      'We use administrative, technical and physical safeguards intended to protect personal information against unauthorized access, use, alteration, disclosure, loss or destruction. Measures may include access controls, authentication, encryption where appropriate, logging, monitoring, backup, vulnerability management and procedures for handling security incidents.',
      'Our safeguards are designed with reference to the nature of the information and the risks involved, including the Protection of Privacy Regulations (Data Security), 2017 where applicable. No website, transmission or storage system is completely secure, and we cannot guarantee absolute security.'
    ]
  },
  {
    id: 'rights',
    title: '12. Your privacy rights',
    paragraphs: [
      'Subject to the Protection of Privacy Law, 1981 and applicable exceptions, you may have the right to access personal information about you held in a database, either personally or through an authorized representative. If the information is inaccurate, incomplete, unclear or outdated, you may request that it be corrected or deleted in accordance with Sections 13 and 14 of the Law.',
      'Depending on the law that applies to you, you may also have additional rights regarding objection, restriction, deletion, portability or withdrawal of consent. To submit a request, contact us using the details below. We may request information needed to verify your identity and authority before responding. We may retain information where required by law or where a lawful basis for retention continues to apply.'
    ]
  },
  {
    id: 'marketing',
    title: '13. Marketing communications',
    paragraphs: [
      'Where permitted by law, we may use contact details to send information about epicfinal services, events, updates and offers. You can opt out of marketing emails at any time by using the unsubscribe option in the message or contacting us. Opting out of marketing does not prevent us from sending necessary service, billing, security or account communications.'
    ]
  },
  {
    id: 'minors',
    title: '14. Children and minors',
    paragraphs: [
      'Our website and business services are not directed to children under 18, and we do not knowingly collect personal information from children for the purpose of providing them with services. If you believe that a child has provided personal information to us without appropriate authorization, please contact us so that we can review the matter.'
    ]
  },
  {
    id: 'changes',
    title: '15. Changes to this Policy',
    paragraphs: [
      'We may update this Privacy Policy to reflect changes in our services, technology, business practices or legal obligations. The updated version will be published on this page with a revised "Last updated" date. Where required by law, we will provide additional notice of material changes.'
    ]
  }
];

const hebrewPolicySections = [
  {
    id: 'he-scope',
    title: '1. תחולה וזהות העסק',
    paragraphs: [
      'מדיניות פרטיות זו מסבירה כיצד epicfinal (להלן: "epicfinal", "אנו" או "העסק") אוספת, משתמשת, שומרת, מעבירה ומגינה על מידע אישי בעת ביקור באתר epicfinal.com, יצירת קשר עמנו, פתיחת חשבון, רכישה או שימוש בשירותי אחסון, ענן, שירותים מנוהלים, פיתוח תוכנה, GenAI ושירותים נלווים.',
      'ביחס למידע אישי הנאסף באמצעות האתר, תהליכי המכירה, הקשר עם הלקוחות והפעילות העסקית שלנו, epicfinal היא בדרך כלל בעלת השליטה במאגר המידע. מדיניות זו מהווה חלק מתנאי השימוש שלנו, ויש לקרוא אותה יחד עם כל טופס הזמנה, הסכם שירות או הודעה ייעודית החלים על השירות שבו נעשה שימוש.'
    ]
  },
  {
    id: 'he-information-you-provide',
    title: '2. מידע שנמסר לנו',
    paragraphs: [
      'מידע אישי עשוי להימסר לנו בעת בקשת הצעת מחיר, קביעת פגישה, מילוי טופס, פתיחת חשבון, רכישת שירות, פנייה לתמיכה, הרשמה לקבלת עדכונים או יצירת קשר עמנו בדרך אחרת.'
    ],
    bullets: [
      'פרטי זיהוי ויצירת קשר, כגון שם, שם העסק, תפקיד, כתובת דוא"ל, מספר טלפון וכתובת.',
      'פרטי חשבון ואימות, כגון שם משתמש, מזהי חשבון ואמצעי אבטחה.',
      'מידע מסחרי ומידע על עסקאות, כגון השירותים שנבחרו, פרטי הזמנה, חשבוניות, מצב תשלום ותכתובות בנושא חיוב.',
      'תוכן של פניות ותכתובות, לרבות בקשות, הודעות, פרטי שיחות או פגישות, מידע טכני ומשוב.',
      'מידע על פרויקטים ושירותים, לרבות הגדרות, דומיינים, פרטי תשתית, קוד, קבצים, הנחיות למערכות בינה מלאכותית, תוכן אפליקציות וחומרים נוספים שנבחר למסור לנו.'
    ]
  },
  {
    id: 'he-collection-notice',
    title: '3. הודעה חשובה בעת מסירת מידע אישי',
    paragraphs: [
      'ככלל, אין חובה חוקית למסור ל-epicfinal מידע אישי, ומסירתו תלויה ברצון ובהסכמה, למעט כאשר מסירת המידע נדרשת לפי דין או מכוח התחייבות חוזית. עם זאת, אי-מסירת מידע שסומן כחובה עלולה למנוע מאיתנו להשיב לפנייה, לפתוח או לנהל חשבון, לטפל בהזמנה, לספק תמיכה או להעניק את השירות המבוקש.',
      'המטרות שלשמן אנו מבקשים מידע, סוגי הגורמים שעשויים לקבל אותו והזכויות ביחס למידע מפורטים במדיניות זו. יש למסור מידע נכון, מלא ומעודכן. מסירת מידע על אדם אחר מחייבת הרשאה חוקית מתאימה ומסירת הודעה לאותו אדם, ככל שהדבר נדרש.'
    ],
    callout: true
  },
  {
    id: 'he-automatic-information',
    title: '4. מידע הנאסף באופן אוטומטי',
    paragraphs: [
      'בעת השימוש באתר או בשירותים, אנו וספקים הפועלים מטעמנו עשויים לאסוף באופן אוטומטי מידע טכני ומידע על אופן השימוש.'
    ],
    bullets: [
      'כתובת IP, מיקום משוער, סוג דפדפן, מערכת הפעלה, סוג מכשיר והגדרות שפה.',
      'עמודים שנצפו, מקור ההפניה, פעולות שבוצעו, מועדי השימוש ונתוני ביצועים או אבחון.',
      'מזהי קובצי Cookie, מזהי פרסום וטכנולוגיות מקוונות דומות.',
      'יומני שירות ואבטחה, רישומי גישה, פעילות API, שימוש במשאבים ומידע הנדרש להפעלה, לניטור ולאבטחת המערכות.'
    ]
  },
  {
    id: 'he-customer-data',
    title: '5. מידע של לקוחות ותפקידנו כספק שירות',
    paragraphs: [
      'לקוחותינו עשויים להעלות, לאחסן, לעבד או להעמיד לרשות השירות מידע אישי הנוגע למשתמשים, לעובדים, ללקוחות או לאנשים אחרים מטעמם (להלן: "מידע לקוח"). כאשר epicfinal מעבדת מידע לקוח אך ורק לצורך מתן שירותים עבור לקוח, הלקוח הוא בדרך כלל הגורם הקובע את מטרות העיבוד ואת אופן העיבוד, והוא האחראי למסירת ההודעות הנדרשות, לקבלת ההסכמות או ההרשאות המתאימות ולוודא שהשימוש בשירותים נעשה בהתאם לדין.',
      'בנסיבות אלה, epicfinal מעבדת את מידע הלקוח בהתאם להוראותיו המתועדות, להסכם החל ולחובות המוטלות עליה לפי דין. אדם המבקש לממש זכות ביחס למידע לקוח נדרש בדרך כלל לפנות תחילה ללקוח הרלוונטי.'
    ]
  },
  {
    id: 'he-uses',
    title: '6. מטרות השימוש במידע אישי',
    bullets: [
      'אספקה, הפעלה, ניהול, תחזוקה, תמיכה ושיפור של האתר והשירותים.',
      'זיהוי ואימות משתמשים וניהול חשבונות, הזמנות, מנויים, חיובים וקשרי לקוחות.',
      'מענה לפניות, תמיכה טכנית ושליחת הודעות שירות, אבטחה ותפעול.',
      'התאמת תוכן, מדידת ביצועים, הבנת דפוסי שימוש ופיתוח שירותים חדשים או משופרים.',
      'הגנה על מערכות, משתמשים ולקוחות; איתור הונאה, שימוש לרעה, חולשות ואירועי אבטחה; ואכיפת הסכמים.',
      'שליחת מסרים שיווקיים כאשר הדבר מותר, בכפוף לבחירות המשתמש ולהוראות הדין.',
      'עמידה בחובות משפטיות, בדרישות חוקיות, בצווים שיפוטיים ובהוראות רגולטוריות, וכן ביסוס או הגנה מפני טענות משפטיות.'
    ]
  },
  {
    id: 'he-sharing',
    title: '7. מסירת מידע לצדדים שלישיים',
    paragraphs: [
      'איננו מוכרים מידע אישי. אנו עשויים למסור מידע רק כאשר הדבר נדרש באופן סביר למטרות המפורטות במדיניות זו, ובין היתר לגורמים הבאים:'
    ],
    bullets: [
      'ספקי תשתית, אחסון, ענן, אבטחת סייבר, ניטור, גיבוי, תקשורת ותמיכה.',
      'כלים עסקיים ואינטגרציות המשמשים לאספקת השירותים, לרבות שירותי תיאום פגישות, ניהול קשרי לקוחות, אנליטיקה, פרסום, מדיה מוטמעת ותשלומים. בהתאם לתכונה שבה נעשה שימוש, ספקים אלה עשויים לכלול את Google, HubSpot, YouTube ו-PayPal.',
      'יועצים מקצועיים, כגון רואי חשבון, מבקרים, מבטחים ויועצים משפטיים, הכפופים לחובות סודיות מתאימות.',
      'רשויות מוסמכות, בתי משפט או גורמים אחרים כאשר המסירה נדרשת לפי דין, לצורך הגנה על זכויות או בטיחות, או לשם בדיקת שימוש לרעה או אירוע אבטחה.',
      'רוכש, חליף או צד רלוונטי במסגרת מיזוג, רכישה, מימון, ארגון מחדש או העברה של העסק, כולו או חלקו, בכפוף לאמצעי הגנה מתאימים.',
      'גורמים אחרים בהתאם להוראה מפורשת או להסכמה שניתנה.'
    ]
  },
  {
    id: 'he-international-transfers',
    title: '8. שמירת מידע והעברתו מחוץ לישראל',
    paragraphs: [
      'המערכות שלנו וספקי השירות שלנו עשויים לשמור או לעבד מידע בישראל ובמדינות אחרות שבהן הם או קבלני המשנה שלהם פועלים. דיני הפרטיות והגנת המידע במדינות אלה עשויים להיות שונים מהדין בישראל.',
      'כאשר מידע אישי מועבר מחוץ לישראל, אנו נוקטים צעדים סבירים שנועדו להבטיח שההעברה תבוצע בהתאם לתקנות הגנת הפרטיות (העברת מידע אל מאגרי מידע שמחוץ לגבולות המדינה), התשס"א-2001, ולהוראות דין נוספות החלות על ההעברה, לרבות באמצעות התחייבויות חוזיות או מנגנון העברה מוכר אחר, ככל שנדרש.'
    ]
  },
  {
    id: 'he-cookies',
    title: '9. קובצי Cookie וטכנולוגיות דומות',
    paragraphs: [
      'אנו משתמשים בקובצי Cookie ובטכנולוגיות דומות לצורך הפעלת האתר, שמירת העדפות, הגנה על חשבונות, הבנת ביצועי האתר, מדידת קמפיינים והפעלת תכונות של צדדים שלישיים. טכנולוגיות אלה עשויות לכלול:'
    ],
    bullets: [
      'קובצי Cookie חיוניים המשמשים לאבטחה, לגישה לחשבון ולפעולות בסיסיות של האתר.',
      'קובצי העדפה ופונקציונליות השומרים בחירות ומאפשרים תכונות מוטמעות.',
      'טכנולוגיות אנליטיקה ופרסום, לרבות שירותי Google, המשמשות להבנת תעבורה ולמדידת יעילות קמפיינים.',
      'קובצי Cookie או אחסון מקומי של צדדים שלישיים בעת שימוש בשירותים כגון HubSpot, YouTube או PayPal.'
    ],
    closing: 'ניתן לשלוט בקובצי Cookie או למחוק אותם באמצעות הגדרות הדפדפן. חסימת קובצי Cookie מסוימים עלולה למנוע מחלקים באתר או בשירותים לפעול כראוי. טכנולוגיות של צדדים שלישיים כפופות גם למדיניות הפרטיות של הספקים הרלוונטיים.'
  },
  {
    id: 'he-retention',
    title: '10. תקופת שמירת המידע',
    paragraphs: [
      'אנו שומרים מידע אישי רק למשך הזמן הנדרש באופן סביר למטרות שלשמן נאסף, לרבות אספקת השירותים, ניהול רשומות עסקיות ואבטחתיות, עמידה בחובות מס, חשבונאות ודין, יישוב מחלוקות ואכיפת הסכמים. תקופת השמירה משתנה בהתאם לסוג המידע, לשירות הרלוונטי, לדרישות הדין ולצרכים תפעוליים או אבטחתיים.',
      'כאשר המידע אינו נדרש עוד, אנו עשויים למחוק אותו, להפוך אותו לאנונימי או להעבירו לארכיון מאובטח, בכפוף לדין ולמגבלות טכניות. עותקי גיבוי עשויים להישמר לתקופה מוגבלת עד להחלפתם במסגרת מחזורי הגיבוי הרגילים.'
    ]
  },
  {
    id: 'he-security',
    title: '11. אבטחת מידע',
    paragraphs: [
      'אנו מפעילים אמצעים ניהוליים, טכנולוגיים ופיזיים שנועדו להגן על מידע אישי מפני גישה, שימוש, שינוי, גילוי, אובדן או השמדה ללא הרשאה. אמצעים אלה עשויים לכלול בקרות גישה, אימות, הצפנה במקרים המתאימים, תיעוד, ניטור, גיבוי, ניהול חולשות ונהלים לטיפול באירועי אבטחה.',
      'אמצעי ההגנה מותאמים לאופי המידע ולסיכונים הכרוכים בעיבודו, בין היתר בהתייחס לתקנות הגנת הפרטיות (אבטחת מידע), התשע"ז-2017, ככל שהן חלות. אף אתר, אמצעי העברה או מערכת אחסון אינם מאובטחים לחלוטין, ולכן אין באפשרותנו להבטיח אבטחה מוחלטת.'
    ]
  },
  {
    id: 'he-rights',
    title: '12. הזכויות ביחס למידע אישי',
    paragraphs: [
      'בכפוף לחוק הגנת הפרטיות, התשמ"א-1981 ולחריגים הקבועים בו, אדם רשאי לעיין במידע אישי עליו המוחזק במאגר מידע, בעצמו או באמצעות נציג שהורשה לכך. אם המידע אינו נכון, שלם, ברור או מעודכן, ניתן לבקש את תיקונו או מחיקתו בהתאם לסעיפים 13 ו-14 לחוק.',
      'בהתאם לדין החל, עשויות לעמוד זכויות נוספות בנוגע להתנגדות לעיבוד, הגבלת עיבוד, מחיקה, ניידות מידע או חזרה מהסכמה. לשם הגשת בקשה יש לפנות אלינו באמצעות הפרטים שלהלן. אנו רשאים לבקש מידע הנדרש לאימות הזהות וההרשאה לפני מתן מענה. מידע עשוי להישמר כאשר קיימת חובה חוקית או עילה חוקית אחרת להמשך שמירתו.'
    ]
  },
  {
    id: 'he-marketing',
    title: '13. דיוור ומסרים שיווקיים',
    paragraphs: [
      'כאשר הדבר מותר לפי דין, אנו עשויים להשתמש בפרטי הקשר לצורך שליחת מידע על שירותי epicfinal, אירועים, עדכונים והצעות. ניתן להסיר את ההרשמה למסרים שיווקיים בכל עת באמצעות קישור ההסרה שבהודעה או בפנייה אלינו. הסרה מדיוור שיווקי אינה מונעת שליחת הודעות הנדרשות לצורכי שירות, חיוב, אבטחה או ניהול החשבון.'
    ]
  },
  {
    id: 'he-minors',
    title: '14. ילדים וקטינים',
    paragraphs: [
      'האתר והשירותים העסקיים שלנו אינם מיועדים לילדים מתחת לגיל 18, ואיננו אוספים ביודעין מידע אישי מילדים לצורך מתן שירותים להם. אם קיים חשש שילד מסר לנו מידע אישי ללא הרשאה מתאימה, ניתן לפנות אלינו כדי שנבדוק את העניין.'
    ]
  },
  {
    id: 'he-changes',
    title: '15. שינויים במדיניות',
    paragraphs: [
      'אנו רשאים לעדכן מדיניות פרטיות זו כדי לשקף שינויים בשירותים, בטכנולוגיה, בפעילות העסקית או בחובות המשפטיות שלנו. הגרסה המעודכנת תפורסם בעמוד זה בציון מועד העדכון. כאשר הדבר נדרש לפי דין, תימסר הודעה נוספת על שינוי מהותי.'
    ]
  }
];

function Privacy() {
  return (
    <div className="privacy-policy-page">
      <header className="privacy-policy-header">
        <div className="privacy-policy-container">
          <div className="privacy-policy-header-row">
            <p className="privacy-policy-kicker">Legal &amp; Trust</p>
            <a className="privacy-language-link" href="#hebrew-version">
              לגרסה בעברית <i className="las la-arrow-down" aria-hidden="true" />
            </a>
          </div>
          <h1>Privacy Policy</h1>
          <p className="privacy-policy-intro">
            How epicfinal handles personal information across our website, cloud platform,
            hosting, managed services and customer communications.
          </p>
          <div className="privacy-policy-meta" aria-label="Policy information">
            <span><i className="las la-calendar" aria-hidden="true" /> Last updated: July 31, 2026</span>
            <span><i className="las la-balance-scale" aria-hidden="true" /> Israeli privacy framework</span>
          </div>
        </div>
      </header>

      <section className="privacy-policy-summary" aria-label="Policy summary">
        <div className="privacy-policy-container privacy-policy-summary-grid">
          <div>
            <span className="privacy-summary-label">Database controller</span>
            <strong>epicfinal, VAT No. 323603605</strong>
          </div>
          <div>
            <span className="privacy-summary-label">Privacy contact</span>
            <a href="mailto:support@epicfinal.com">support@epicfinal.com</a>
          </div>
          <div>
            <span className="privacy-summary-label">Location</span>
            <strong>Beer-Sheva 84709, Israel</strong>
          </div>
        </div>
      </section>

      <div className="privacy-policy-container privacy-policy-layout">
        <aside className="privacy-policy-sidebar">
          <nav aria-label="Privacy Policy contents">
            <p>On this page</p>
            <ol>
              {policySections.map((section) => (
                <li key={section.id}>
                  <a href={`#${section.id}`}>{section.title.replace(/^\d+\.\s*/, '')}</a>
                </li>
              ))}
              <li><a href="#contact">Contact us</a></li>
            </ol>
          </nav>
        </aside>

        <main className="privacy-policy-content" id="privacy-policy-content">
          <div className="privacy-policy-notice">
            <i className="las la-shield-alt" aria-hidden="true" />
            <div>
              <strong>Your privacy matters</strong>
              <p>
                We collect information for defined business and service purposes, limit access
                to authorized parties, and provide channels for privacy requests.
              </p>
            </div>
          </div>

          {policySections.map((section) => (
            <section
              key={section.id}
              id={section.id}
              className={`privacy-policy-section${section.callout ? ' privacy-policy-section-emphasis' : ''}`}
            >
              <h2>{section.title}</h2>
              {section.paragraphs?.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
              {section.bullets && (
                <ul>
                  {section.bullets.map((item, index) => <li key={index}>{item}</li>)}
                </ul>
              )}
              {section.closing && <p>{section.closing}</p>}
            </section>
          ))}

          <section id="contact" className="privacy-policy-section privacy-policy-contact">
            <p className="privacy-policy-kicker">Privacy requests</p>
            <h2>16. Contact us</h2>
            <p>
              For access or correction requests, questions, complaints or concerns about this
              Privacy Policy or our handling of personal information, contact:
            </p>
            <address>
              <strong>epicfinal</strong><br />
              VAT No. 323603605<br />
              South District, Beer-Sheva 84709, Israel<br />
              <a href="mailto:support@epicfinal.com">support@epicfinal.com</a>
            </address>
          </section>
        </main>
      </div>

      <section id="hebrew-version" className="privacy-hebrew-header" dir="rtl">
        <div className="privacy-policy-container">
          <p className="privacy-policy-kicker">משפטי ופרטיות</p>
          <h2>מדיניות פרטיות</h2>
          <p>
            מידע על האופן שבו epicfinal אוספת, משתמשת, שומרת ומגינה על מידע אישי
            באתר, בפלטפורמת הענן, בשירותי האחסון, בשירותים המנוהלים ובתקשורת עם לקוחות.
          </p>
          <div className="privacy-policy-meta" aria-label="פרטי המדיניות">
            <span><i className="las la-calendar" aria-hidden="true" /> עדכון אחרון: 31 ביולי 2026</span>
            <span><i className="las la-balance-scale" aria-hidden="true" /> בהתאם למסגרת דיני הפרטיות בישראל</span>
          </div>
        </div>
      </section>

      <section className="privacy-policy-summary privacy-policy-summary-he" aria-label="תקציר המדיניות" dir="rtl">
        <div className="privacy-policy-container privacy-policy-summary-grid">
          <div>
            <span className="privacy-summary-label">בעלת השליטה במאגר</span>
            <strong>epicfinal, עוסק מורשה מס&apos; 323603605</strong>
          </div>
          <div>
            <span className="privacy-summary-label">פניות בנושא פרטיות</span>
            <a href="mailto:support@epicfinal.com">support@epicfinal.com</a>
          </div>
          <div>
            <span className="privacy-summary-label">מיקום</span>
            <strong>באר שבע 84709, ישראל</strong>
          </div>
        </div>
      </section>

      <div className="privacy-policy-container privacy-policy-layout privacy-policy-hebrew" dir="rtl">
        <aside className="privacy-policy-sidebar">
          <nav aria-label="תוכן עניינים של מדיניות הפרטיות">
            <p>בעמוד זה</p>
            <ol>
              {hebrewPolicySections.map((section) => (
                <li key={section.id}>
                  <a href={`#${section.id}`}>{section.title.replace(/^\d+\.\s*/, '')}</a>
                </li>
              ))}
              <li><a href="#he-contact">יצירת קשר</a></li>
            </ol>
          </nav>
        </aside>

        <main className="privacy-policy-content">
          <div className="privacy-policy-notice">
            <i className="las la-shield-alt" aria-hidden="true" />
            <div>
              <strong>הפרטיות שלך חשובה לנו</strong>
              <p>
                אנו אוספים מידע למטרות עסקיות ותפעוליות מוגדרות, מגבילים את הגישה
                לגורמים מורשים ומעמידים ערוץ מסודר להגשת בקשות בנושא פרטיות.
              </p>
            </div>
          </div>

          {hebrewPolicySections.map((section) => (
            <section
              key={section.id}
              id={section.id}
              className={`privacy-policy-section${section.callout ? ' privacy-policy-section-emphasis' : ''}`}
            >
              <h2>{section.title}</h2>
              {section.paragraphs?.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
              {section.bullets && (
                <ul>
                  {section.bullets.map((item, index) => <li key={index}>{item}</li>)}
                </ul>
              )}
              {section.closing && <p>{section.closing}</p>}
            </section>
          ))}

          <section id="he-contact" className="privacy-policy-section privacy-policy-contact">
            <p className="privacy-policy-kicker">בקשות בנושא פרטיות</p>
            <h2>16. יצירת קשר</h2>
            <p>
              לבקשות עיון או תיקון, לשאלות, לתלונות או לכל פנייה הנוגעת למדיניות זו
              או לאופן הטיפול שלנו במידע אישי, ניתן ליצור קשר עם:
            </p>
            <address>
              <strong>epicfinal</strong><br />
              עוסק מורשה מס&apos; 323603605<br />
              מחוז הדרום, באר שבע 84709, ישראל<br />
              <a href="mailto:support@epicfinal.com">support@epicfinal.com</a>
            </address>
          </section>
        </main>
      </div>
    </div>
  );
}

export default Privacy;
